import clsx from 'clsx'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import noop from 'lib/function/noop'
import shallowEqualWithNestedProps from 'lib/memoize/shallowEqualWithNestedProps'
import { rem } from 'polished'
import type { ComponentProps, PropsWithChildren } from 'react'
import { memo, useCallback, useContext } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import TextButton from '../Button/TextButton'
import { LuxKitModalBehaviourStateContext } from './context/LuxKitModalBehaviourContexts'

export const StyledModalFooter = styled.footer`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: flex;
  gap: ${rem(8)};
  transition: box-shadow .2s ease;
  z-index: 1;
  padding: ${rem(20)};
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(24)} ${rem(24)};
  }

  ${mediaQueryOnly.mobile} {
    &.layout-auto.has-buttons {
      justify-content: flex-end;
    }
  }
  ${mediaQueryUp.tablet} {
    &.tablet-layout-auto.has-buttons {
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
    }
  }

  &.has-shadow {
    box-shadow: ${props => props.theme.shadow.top.small};
  }
`

const ActionsContainer = styled.div`
  display: inline-flex;
  gap: ${rem(8)};
  flex-shrink: 0;

  ${mediaQueryOnly.mobile} {
    &.layout-auto {
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: end;

      > * {
        flex-grow: 1;
      }
    }
    &.layout-stacked {
      flex-direction: column;
    }
  }
  ${mediaQueryUp.tablet} {
    &.tablet-layout-auto {
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: end;
    }
    &.tablet-layout-stacked {
      flex-direction: column;
    }
  }
`

export type ModalFooterOnClickHandler = (e: React.MouseEvent<HTMLButtonElement>, resolve: (result?: any) => void) => void

export interface ModalFooterActionProps extends Omit<ComponentProps<typeof TextButton>, 'onClick' | 'kind' | 'variant' | 'size' | 'fit'> {
  onClick?: ModalFooterOnClickHandler
}

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  isPrimaryActionHidden?: boolean
  isSecondaryActionHidden?: boolean
  primaryActionProps?: ModalFooterActionProps
  secondaryActionProps?: ModalFooterActionProps
  /**
   * @default auto
   */
  actionsLayout?: 'auto' | 'stacked'
  /**
   * @default `actionsLayout`
   */
  tabletActionsLayout?: 'auto' | 'stacked'
}

function ModalFooter({
  children,
  isPrimaryActionHidden,
  isSecondaryActionHidden,
  primaryActionProps,
  secondaryActionProps,
  actionsLayout = 'auto',
  tabletActionsLayout,
  'data-testid': dataTestId,
  ...rest
}: Props) {
  const modalContext = useModalElementContext()
  const modalBehaviourState = useContext(LuxKitModalBehaviourStateContext)
  const hasPrimaryButton = primaryActionProps?.children && !isPrimaryActionHidden
  const hasSecondaryButton = secondaryActionProps?.children && !isSecondaryActionHidden
  const hasButtons = hasPrimaryButton || hasSecondaryButton

  const onPrimaryButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (primaryActionProps?.onClick) {
      primaryActionProps.onClick(e, modalContext?.resolve ?? noop)
    } else if (e.currentTarget.type !== 'submit') {
      // if it's a submit button, assume 'default' behaviour is handled by some sort of form element
      modalContext?.resolve()
    }
  }, [modalContext, primaryActionProps])

  const onSecondaryButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (secondaryActionProps?.onClick) {
      secondaryActionProps.onClick(e, modalContext?.resolve ?? noop)
    } else if (e.currentTarget.type !== 'submit') {
      // if it's a submit button, assume 'default' behaviour is handled by some sort of form element
      modalContext?.resolve()
    }
  }, [modalContext, secondaryActionProps])

  return <StyledModalFooter
    {...rest}
    data-testid={dataTestId}
    className={clsx(
      `layout-${actionsLayout}`,
      `tablet-layout-${tabletActionsLayout ?? actionsLayout}`,
      {
        'has-shadow': !(modalBehaviourState?.bodyHasReachedEnd ?? true),
        'has-buttons': hasButtons,
      },
    )}
  >
    {children}
    {hasButtons && <ActionsContainer
      className={clsx(
        `layout-${actionsLayout}`,
        `tablet-layout-${tabletActionsLayout ?? actionsLayout}`,
      )}
    >
      {hasPrimaryButton && <TextButton
        data-testid={dataTestId ? `${dataTestId}-primary-action` : undefined}
        {...primaryActionProps}
        kind="primary"
        size="large"
        onClick={onPrimaryButtonClick}
      />}
      {hasSecondaryButton && <TextButton
        data-testid={dataTestId ? `${dataTestId}-secondary-action` : undefined}
        {...secondaryActionProps}
        kind="tertiary"
        size="large"
        onClick={onSecondaryButtonClick}
      />}
    </ActionsContainer>}
  </StyledModalFooter>
}

export default memo(ModalFooter, shallowEqualWithNestedProps(['primaryActionProps', 'secondaryActionProps']))
